import { useThemePerformance } from '~/hooks/use-theme-overview/useThemeOverview';
import { useGetThemePerformance } from '~/hooks/useGetThemePerformance';
import { formatNumber } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/number-formatter';

import { Horizon } from '../ThemePerformanceSection';

type UseThemePerformanceReturnProps = {
  entityTags: string[];
  benchmarkTags?: string[];
  numDays?: number; // Defaults to 1 year
};

export const getReturnValue = ({
  value,
  benchmarkValue,
  withBenchmark,
}: {
  value: number | undefined;
  benchmarkValue?: number;
  withBenchmark?: boolean;
}) => {
  if (withBenchmark) {
    if (benchmarkValue !== undefined && value !== undefined) {
      return value - benchmarkValue;
    } else {
      return undefined;
    }
  }

  return value;
};

export const useThemePerformanceReturn = ({
  entityTags,
  benchmarkTags,
  numDays,
}: UseThemePerformanceReturnProps) => {
  const { data, isFetching } = useThemePerformance(entityTags, numDays);
  const { data: benchmarkData, isFetching: isBenchmarkFetching } =
    useThemePerformance(benchmarkTags, numDays);
  const value = data?.aggregate?.at(-1)?.value;
  const benchmarkValue = benchmarkData?.aggregate?.at(-1)?.value;

  const returnValue = getReturnValue({
    value,
    benchmarkValue,
    withBenchmark: !!benchmarkTags?.length,
  });

  return {
    isLoading: isFetching || isBenchmarkFetching,
    value: formatNumber({ isPercentage: true, value: returnValue }),
    isNegative: !!returnValue && returnValue < 0,
  };
};

export const useRevampThemePerformanceReturn = ({
  themeId,
  selectedHorizon,
  benchmarkTags,
  numDays,
}: {
  themeId: string;
  selectedHorizon: Horizon;
  benchmarkTags?: string[];
  numDays?: number;
}) => {
  const { data, isFetching } = useGetThemePerformance({ themeId });
  const performanceData = data?.performance as
    | Record<string, number>
    | undefined;
  const horizonReturn = performanceData?.[selectedHorizon.toLowerCase()];

  const { data: benchmarkData, isFetching: isBenchmarkFetching } =
    useThemePerformance(benchmarkTags, numDays);
  const benchmarkValue = benchmarkData?.aggregate?.at(-1)?.value;

  const returnValue = getReturnValue({
    value: horizonReturn,
    benchmarkValue,
    withBenchmark: !!benchmarkTags?.length,
  });

  return {
    isLoading: isFetching || isBenchmarkFetching,
    value: formatNumber({ isPercentage: true, value: returnValue }),
    isNegative: !!returnValue && returnValue < 0,
  };
};
